import { showGlobalError } from "../context/ErrorContext";
import { clearFrontendAuthData } from "../utils/AuthUtil";
import { getGlobalNavigationService } from "../context/NavigationContext";
import { isTokenExpired } from "../../../utils/jwtUtils";
import { API_BASE_URL } from "../../../config/apiConfig";

// Perform a silent logout + redirect to /login. Used both when the backend
// signals "redirect_to_login" on a 401 and when any backend call fails after
// the JWT has already expired (e.g. 500/502/504 from a downstream filter).
const redirectToLoginSilently = (reason) => {
  console.log(`🔄 APIService: Silent login redirect (${reason})`);

  // Avoid clobbering an in-flight authentication attempt.
  if (window.authenticationPromise) {
    console.log(
      "⏸️ APIService: Authentication in progress, skipping silent redirect",
    );
    return;
  }

  clearFrontendAuthData();

  if (window.userRoleValidationInterval) {
    clearInterval(window.userRoleValidationInterval);
    window.userRoleValidationInterval = null;
  }

  const loginUrl = "/login";
  const navigationService = getGlobalNavigationService();
  if (navigationService) {
    try {
      navigationService.navigate(loginUrl);
    } catch (navError) {
      console.error("❌ APIService: Navigation failed:", navError);
      window.location.href = loginUrl;
    }
  } else {
    window.location.href = loginUrl;
  }
};

/**
 * APIService - Enhanced with AuthTokenValidationFilter Integration
 *
 * This service automatically handles invalid JWT tokens from the backend's
 * AuthTokenValidationFilter by:
 * 1. Detecting structured 401 responses with "redirect_to_login" action
 * 2. Clearing all authentication data (localStorage, sessionStorage, cookies)
 * 3. Automatically redirecting to login page without showing error popups
 * 4. Maintaining compatibility with existing UserContext error handling
 *
 * For regular 401/403 errors (not from AuthTokenValidationFilter),
 * the standard error popup flow is preserved for UserContext integration.
 */
const BASE_URL = API_BASE_URL;
const API_KEY = process.env.REACT_APP_API_KEY || "API_KEY not defined";

// Default timeout for API requests (in milliseconds)
const DEFAULT_TIMEOUT = 30000; // 30 seconds

// ─── GET request deduplication ────────────────────────────────────────────────
// Prevents concurrent identical GET requests from hitting the server multiple
// times.  If a GET for the same endpoint is already in-flight, subsequent
// callers receive the same Promise (and therefore the same resolved value).
// The entry is removed as soon as the request settles, so later calls still
// hit the network normally.  Only GETs are deduplicated — mutations (POST,
// PUT, DELETE) are always sent.
const inflightGETs = new Map();

// ─── Session-scoped auth token store ────────────────────────────────────────
// Why this exists:
//   The HttpOnly `authToken` cookie set by api.kinprotect.com is the primary
//   auth mechanism. On most browsers it is sent automatically with every
//   credentialed fetch from shield.kinprotect.com (same eTLD+1, SameSite=Lax).
//   On some configurations (notably Chrome on macOS with Privacy Sandbox or
//   third-party-cookie blocking enabled), the cookie is silently dropped on
//   cross-origin sub-resource requests, causing every API call to return 401.
//
//   To make login work universally we mirror the JWT into module memory and
//   sessionStorage, then send it as `Authorization: Bearer <token>`. The
//   backend's AuthTokenValidationFilter checks the Authorization header first
//   and falls back to the cookie, so both paths transparently work.
//
// Security properties:
//   • sessionStorage is tab-isolated — other origins cannot read it, and other
//     tabs of the same origin get their own copy.
//   • sessionStorage is automatically cleared when the tab/window closes, so
//     no bearer credential persists across browser sessions.
//   • sessionStorage survives page refresh within the same tab, matching the
//     UX users get on browsers where the HttpOnly cookie path works.
//   • XSS exposure: an attacker running JS in the active page can already make
//     authenticated calls via the HttpOnly cookie or fetch() with credentials,
//     so reading sessionStorage does not meaningfully expand the attack surface
//     during an active session. Persisted localStorage would be strictly worse
//     because the token would survive across browser restarts.
const _SESSION_TOKEN_KEY = "ks_st"; // short, opaque key

let _memoryAuthToken = (() => {
  // Restore on module load (handles page refresh in the same tab).
  try {
    return sessionStorage.getItem(_SESSION_TOKEN_KEY) || null;
  } catch {
    return null; // sessionStorage may be unavailable in some private modes
  }
})();

export const setInMemoryToken = (token) => {
  _memoryAuthToken = token || null;
  try {
    if (token) {
      sessionStorage.setItem(_SESSION_TOKEN_KEY, token);
    } else {
      sessionStorage.removeItem(_SESSION_TOKEN_KEY);
    }
  } catch {
    /* sessionStorage unavailable — in-memory copy still works for the tab */
  }
};

export const clearInMemoryToken = () => {
  _memoryAuthToken = null;
  try {
    sessionStorage.removeItem(_SESSION_TOKEN_KEY);
  } catch {
    /* ignore */
  }
  // Also clean up any token written to localStorage by an older app version.
  try {
    localStorage.removeItem("authToken");
  } catch {
    /* ignore */
  }
};

// Helper function to create an AbortController with timeout
const createTimeoutController = (timeoutMs = DEFAULT_TIMEOUT) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  return { controller, timeoutId };
};

// Helper function to get cookie value by name
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(";").shift();
  }
  return null;
};

// Helper function to build headers with optional Authorization and CSRF token
const buildHeaders = (additionalHeaders = {}, method = "GET") => {
  const headers = {
    ...additionalHeaders,
  };

  // Only add API key if it's defined and not the default fallback
  if (API_KEY && API_KEY !== "API_KEY not defined") {
    headers["x-api-key"] = API_KEY;
  }

  // Auth token resolution order (most-to-least preferred):
  //   1. In-memory token (mirrored from sessionStorage on module load) — set at
  //      login, cleared at logout, never persisted to localStorage.
  //   2. HttpOnly authToken cookie — sent automatically by the browser on
  //      browsers that honour the cross-origin same-site cookie. The backend
  //      reads this directly; we only inspect it here for the legacy code path.
  //   3. Legacy localStorage entry — cleared on logout. Kept here only so
  //      sessions written by an older version of the app continue to work
  //      until the user logs out once.
  let authToken =
    _memoryAuthToken ||
    getCookie("authToken") ||
    localStorage.getItem("authToken") ||
    null;

  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  // Add CSRF token ONLY for state-changing requests (POST, PUT, PATCH, DELETE)
  // Spring Security uses XSRF-TOKEN cookie and expects X-XSRF-TOKEN header
  // GET requests don't need CSRF protection as they should be idempotent
  const methodUpper = method.toUpperCase();
  if (["POST", "PUT", "PATCH", "DELETE"].includes(methodUpper)) {
    const csrfToken = getCookie("XSRF-TOKEN");
    if (csrfToken) {
      headers["X-XSRF-TOKEN"] = csrfToken;
      console.log(`[APIService] CSRF token added to ${methodUpper} request`);
    }
  }

  return headers;
};

// Helper function to handle HTTP errors globally
const handleHttpError = async (response, endpoint, method) => {
  const statusCode = response.status;

  // Session-timeout short-circuit:
  //   When the JWT has genuinely expired, a downstream filter can reject the
  //   call with a 5xx (500/502/503/504) instead of a clean 401. In that narrow
  //   case the user did nothing wrong — their session just timed out — so
  //   suppress the global error popup and quietly send them to /login.
  //
  //   IMPORTANT: this guard is intentionally limited to 5xx responses only.
  //   It must NOT fire on 401/403/404, because:
  //     • isTokenExpired() reads localStorage.jwtExpiry, which can be stale
  //       (read as "expired") even while the real session — carried by the
  //       HttpOnly authToken cookie — is still valid on the backend. A page
  //       refresh re-authenticates via that cookie, proving the session lives.
  //     • Authorization failures (e.g. a 401/404 from an admin-only endpoint
  //       such as /api/v1/demo/admin/*) would otherwise be misread as a session
  //       timeout and force a spurious logout. Genuine token invalidation is
  //       already handled below by the dedicated 401 `redirect_to_login` block.
  const isDownstream5xx = [500, 502, 503, 504].includes(statusCode);
  if (
    isDownstream5xx &&
    !endpoint.includes("/auth/") &&
    !endpoint.includes("/login") &&
    isTokenExpired()
  ) {
    redirectToLoginSilently(`token expired before ${method} ${endpoint}`);
    const sessionError = new Error("Session expired");
    sessionError.silent = true;
    sessionError.code = "SESSION_EXPIRED";
    throw sessionError;
  }

  // Special case: 403 from ExtensionReviewAccessFilter for an extension_review test seat.
  //   Chrome Web Store reviewer seats are intentionally restricted server-side to the
  //   browser-extension endpoints. When the reviewer logs into the web app, the shell
  //   fires background calls (dashboard, simulation stats, etc.) that the filter blocks
  //   with 403 {"error":"extension_review_restricted"}. Those are expected — suppress the
  //   global "Access Denied" popup and let the caller's try/catch handle it silently, so
  //   the reviewer can still sign in and use the extension without an error dialog.
  if (statusCode === 403) {
    const restrictedClone = response.clone();
    const restrictedBody = await restrictedClone.json().catch(() => null);
    if (
      restrictedBody &&
      restrictedBody.error === "extension_review_restricted"
    ) {
      console.log(
        "⚠️ APIService: extension_review restricted endpoint — suppressing popup",
        endpoint,
      );
      const error = new Error(
        restrictedBody.message || "Restricted test account",
      );
      error.response = response;
      error.status = 403;
      error.silent = true;
      throw error;
    }
  }

  // Special case: 403 on account-ownership endpoint is expected for non-account-owners
  // Don't show error popup, just throw the error silently for the caller to handle
  if (statusCode === 403 && endpoint.includes("/account-ownership")) {
    console.log(
      "⚠️ APIService: 403 on account-ownership endpoint (expected for member users)",
    );
    const error = new Error("Not an account owner");
    error.response = {
      status: 403,
      statusText: "Forbidden",
      data: { message: "Not an account owner" },
    };
    throw error;
  }

  // Special case: 403 on subscription history endpoint is expected for non-owners.
  //   GET /api/v1/app/subscriptions/user/history is owner-only (account_admin /
  //   platform_admin). SubscriptionContext probes it to distinguish a lapsed
  //   account from a never-subscribed one, but a member (non-owner) seat always
  //   gets a 403 here. That's expected and already handled by the context's
  //   fail-open catch, so suppress the global Access Denied popup and let the
  //   caller's try/catch handle it silently.
  if (
    statusCode === 403 &&
    endpoint.includes("/api/v1/app/subscriptions/user/history")
  ) {
    console.log(
      "⚠️ APIService: 403 on subscription history endpoint (expected for non-owner members)",
    );
    const error = new Error("Not authorized to view subscription history");
    error.response = {
      status: 403,
      statusText: "Forbidden",
      data: { message: "Not authorized to view subscription history" },
    };
    error.silent = true;
    throw error;
  }

  // Special case: 403 on my-group/count endpoint is expected for non-admin users
  // Regular member users don't have group_admin role, so this is expected
  // Don't show error popup, just throw the error silently for the caller to handle
  if (statusCode === 403 && endpoint.includes("/my-group/count")) {
    console.log(
      "⚠️ APIService: 403 on my-group/count endpoint (expected for non-admin users)",
    );
    const error = new Error("Not authorized to view group count");
    error.response = {
      status: 403,
      statusText: "Forbidden",
      data: { message: "Not authorized to view group count" },
    };
    throw error;
  }

  // Special case: auth failure on the optional Demo Users admin panel.
  //   The Demo Users panel (/api/v1/demo/admin/*) fires JWT-protected GETs the
  //   moment it mounts. If that single request happens to reach the backend
  //   without a usable JWT (e.g. the session-scoped Bearer token isn't present
  //   in this tab, or the cross-origin authToken cookie was dropped), the
  //   backend replies 401 with action:"redirect_to_login". Letting that flow
  //   into the global redirect would tear down the ENTIRE app session — even
  //   though the user's broader session is still valid — and bounce them to
  //   /login. A refresh then silently re-validates and puts them back, which is
  //   exactly the confusing "Demo Users logs me out" symptom.
  //
  //   Loading one optional admin panel must never log the whole app out. Throw
  //   the error silently so DemoAdminPanel's own try/catch surfaces a toast and
  //   the user stays where they are.
  //
  //   Exception: a 401 "No authentication token found" means the session is
  //   genuinely gone (same-origin API sends the cookie on every request), so it
  //   falls through to the redirect_to_login handling below.
  const demoAdminBody =
    statusCode === 401 && endpoint.includes("/api/v1/demo/admin/")
      ? await response
          .clone()
          .json()
          .catch(() => null)
      : null;
  const demoAdminSessionGone =
    demoAdminBody?.action === "redirect_to_login" &&
    demoAdminBody?.message === "No authentication token found";
  if (
    (statusCode === 401 || statusCode === 403) &&
    endpoint.includes("/api/v1/demo/admin/") &&
    !demoAdminSessionGone
  ) {
    console.log(
      `⚠️ APIService: ${statusCode} on demo admin endpoint — surfacing to caller without global logout`,
    );
    const error = new Error("Unable to load demo users");
    error.response = response;
    error.status = statusCode;
    error.silent = true;
    throw error;
  }

  // Check for AuthTokenValidationFilter 401 responses FIRST (before showing error popup)
  if (statusCode === 401) {
    console.log("🔍 APIService: Processing 401 response...");
    try {
      // Clone the response so we can read it multiple times if needed
      const responseClone = response.clone();
      const errorData = await responseClone.json().catch(() => null);
      console.log("🔍 APIService: Parsed error data:", errorData);

      // Check if this is an AuthTokenValidationFilter response
      // Only redirect to login if backend explicitly sends action: "redirect_to_login"
      // This distinguishes between:
      // 1. Invalid/expired JWT tokens (redirect to login)
      // 2. Missing/wrong API keys (show error, don't logout)
      // 3. Authorization failures from @RequireRole (show error, don't logout)
      if (errorData && errorData.action === "redirect_to_login") {
        console.log(
          "🚨 APIService: AuthTokenValidationFilter detected invalid/expired token",
        );
        console.log("📍 Blocked endpoint:", errorData?.path || endpoint);
        console.log("💬 Server message:", errorData?.message);
        console.log("🕒 Server timestamp:", errorData?.timestamp);

        // Check if authentication is in progress to avoid race condition
        if (window.authenticationPromise) {
          console.log(
            "⏸️ APIService: Authentication in progress, not clearing auth data to avoid race condition",
          );
          // Return the error without clearing auth data
          const error = new Error(
            errorData?.message || "Authentication required",
          );
          error.response = response;
          error.status = 401;
          error.data = errorData;
          throw error;
        }

        redirectToLoginSilently("backend redirect_to_login");

        // Return early - don't show error popup or throw error since we're redirecting
        console.log("🔄 APIService: Returning early, no popup should appear");
        return;
      } else {
        console.log(
          "🔍 APIService: Not an AuthTokenValidationFilter response, proceeding with normal error handling",
        );
        console.log("🔍 APIService: errorData.action =", errorData?.action);
      }
    } catch (parseError) {
      console.log(
        "⚠️ APIService: Could not parse 401 response as JSON, treating as regular 401",
      );
      console.error("Parse error:", parseError);
      // Fall through to regular 401 handling
    }
  }

  // For all other errors (including regular 401s), show the global error popup
  let errorMessage = response.statusText;
  let actionName = `${method.toLowerCase()} ${endpoint}`;

  if ([401, 403, 404, 429, 500, 502, 503, 504].includes(statusCode)) {
    try {
      // Clone the response to avoid consuming the stream multiple times
      const responseClone = response.clone();
      const errorData = await responseClone.json().catch(() => null);
      if (errorData) {
        if (typeof errorData === "string") {
          errorMessage = errorData;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      }
    } catch (e) {
      // If we can't parse the response, use the status text
    }

    // Show the global error popup and wait for user to acknowledge
    await showGlobalError(statusCode, errorMessage, actionName);
  } else {
    // For status codes not in the list above, still try to parse error message
    try {
      const responseClone = response.clone();
      const errorData = await responseClone.json().catch(() => null);
      if (errorData) {
        if (typeof errorData === "string") {
          errorMessage = errorData;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      }
    } catch (e) {
      // If we can't parse the response, use the status text
    }
  }

  // After showing the popup (or if it's not a handled status code),
  // throw the error with the specific error message for the calling code to handle
  const error = new Error(
    errorMessage || `${method} request failed: ${response.statusText}`,
  );
  error.response = {
    status: response.status,
    statusText: response.statusText,
    data: await response
      .clone()
      .json()
      .catch(() => ({ message: response.statusText })),
  };
  throw error;
};

// Wrapper for handling authentication race conditions
const withAuthRetry = async (requestFn) => {
  // If authentication is in progress, wait for it to complete
  if (window.authenticationPromise || window.authenticationStarting) {
    console.log(
      "⏳ APIService: Authentication in progress, waiting for completion...",
      {
        authenticationStarting: window.authenticationStarting,
        authenticationPromise: !!window.authenticationPromise,
      },
    );

    if (window.authenticationPromise) {
      try {
        const authComplete = await window.authenticationPromise;
        if (authComplete) {
          console.log(
            "✅ APIService: Authentication complete, proceeding with request",
          );
        } else {
          console.warn("⚠️ APIService: Authentication failed or timed out");
        }
      } catch (error) {
        console.error(
          "❌ APIService: Error waiting for authentication:",
          error,
        );
      }
    }
  }

  // Now execute the request
  return await requestFn();
};

const APIService = {
  get: async (endpoint) => {
    // ── Deduplication: reuse an identical in-flight GET ──────────────────
    if (inflightGETs.has(endpoint)) {
      console.log(
        `[APIService] GET ${endpoint} — reusing in-flight request (dedup)`,
      );
      return inflightGETs.get(endpoint);
    }

    const doGet = async () => {
      console.log(`[APIService] GET request to: ${BASE_URL}${endpoint}`);

      const { controller, timeoutId } = createTimeoutController();

      try {
        const headers = buildHeaders({}, "GET");

        const response = await fetch(`${BASE_URL}${endpoint}`, {
          headers,
          signal: controller.signal,
          credentials: "include", // Include cookies in request and response
        });

        // Clear the timeout since request completed
        clearTimeout(timeoutId);

        console.log(
          `[APIService] Response status: ${response.status} ${response.statusText}`,
        );

        console.log("Response: ", response);
        console.log(
          `[APIService] Response headers:`,
          Object.fromEntries([...response.headers.entries()]),
        );

        if (response.status === 404) {
          console.error(
            `[APIService] GET request failed: ${response.statusText}`,
          );
          console.error(`[APIService] Response details:`, {
            status: response.status,
            statusText: response.statusText,
            headers: Object.fromEntries([...response.headers.entries()]),
          });

          return null;
          // Return null for 404 errors. This allows the caller to handle it gracefully. In the case
          // of  SimulationProfileCustom.js, returning null allows the component to display an empty
          // Simulation Profile form so user can fill it out.
        }

        if (!response.ok) {
          console.error(
            `[APIService] GET request failed: ${response.statusText}`,
          );
          await handleHttpError(response, endpoint, "GET");
        }

        const data = await response.json();
        console.log(
          `[APIService] GET request successful, data type: ${
            Array.isArray(data) ? "array" : typeof data
          }`,
        );
        console.log(
          `[APIService] Response data sample:`,
          Array.isArray(data)
            ? `Array with ${data.length} items` +
                (data.length > 0
                  ? `, first item keys: ${Object.keys(data[0]).join(", ")}`
                  : "")
            : typeof data === "object"
              ? `Object with keys: ${Object.keys(data).join(", ")}`
              : `Value: ${JSON.stringify(data).substring(0, 100)}${
                  JSON.stringify(data).length > 100 ? "..." : ""
                }`,
        );

        return data;
      } catch (error) {
        // Clear the timeout in case of error
        clearTimeout(timeoutId);

        if (error.name === "AbortError") {
          console.error(
            `[APIService] GET request to ${endpoint} timed out after ${DEFAULT_TIMEOUT}ms`,
          );
          const timeoutError = new Error(
            `Request timed out after ${DEFAULT_TIMEOUT / 1000} seconds`,
          );
          timeoutError.code = "TIMEOUT";
          throw timeoutError;
        }

        console.error(
          `[APIService] Error in GET request to ${endpoint}:`,
          error,
        );
        console.error("[APIService] Error stack:", error.stack);
        throw error;
      }
    };

    // Store the promise so concurrent callers share the same request
    const promise = withAuthRetry(() => doGet()).finally(() => {
      inflightGETs.delete(endpoint);
    });
    inflightGETs.set(endpoint, promise);
    return promise;
  },
  post: async (endpoint, data, options = {}) => {
    console.log(`${BASE_URL}${endpoint}`); // Log the endpoint being accessed
    console.log("APIService post data:", data);
    console.log(
      "APIService post data type:",
      Array.isArray(data) ? "Array" : typeof data,
    );
    console.log("APIService post data (JSON):", JSON.stringify(data)); // Log the data being sent

    return withAuthRetry(async () => {
      const { controller, timeoutId } = createTimeoutController(
        options.timeout || DEFAULT_TIMEOUT,
      );

      try {
        // Determine content type and body based on data type
        let contentType = "application/json";
        let body = JSON.stringify(data);

        // Handle URLSearchParams (form data)
        if (data instanceof URLSearchParams) {
          contentType = "application/x-www-form-urlencoded";
          body = data.toString();
        }

        // Allow options to override content type
        if (options.headers && options.headers["Content-Type"]) {
          contentType = options.headers["Content-Type"];
        }

        const headers = buildHeaders(
          {
            "Content-Type": contentType,
            ...options.headers, // Merge any additional headers
          },
          "POST",
        );

        const response = await fetch(`${BASE_URL}${endpoint}`, {
          method: "POST",
          headers,
          body: body,
          signal: controller.signal,
          credentials: "include", // Include cookies in request and response
        });

        // Clear the timeout since request completed
        clearTimeout(timeoutId);

        if (!response.ok) {
          await handleHttpError(response, endpoint, "POST");
        }
        return response.json();
      } catch (error) {
        // Clear the timeout in case of error
        clearTimeout(timeoutId);

        if (error.name === "AbortError") {
          console.error(
            `[APIService] POST request to ${endpoint} timed out after ${DEFAULT_TIMEOUT}ms`,
          );
          const timeoutError = new Error(
            `Request timed out after ${DEFAULT_TIMEOUT / 1000} seconds`,
          );
          timeoutError.code = "TIMEOUT";
          throw timeoutError;
        }

        throw error;
      }
    });
  },
  put: async (endpoint, data) => {
    const headers = buildHeaders(
      {
        "Content-Type": "application/json",
      },
      "PUT",
    );

    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(data),
      credentials: "include", // Include cookies in request and response
    });
    if (!response.ok) {
      await handleHttpError(response, endpoint, "PUT");
    }
    return response.json();
  },
  delete: async (endpoint, data) => {
    const headers = buildHeaders({}, "DELETE");

    // If data is provided, add Content-Type header
    if (data) {
      headers["Content-Type"] = "application/json";
    }

    const options = {
      method: "DELETE",
      headers,
      credentials: "include", // Include cookies in request and response
    };

    // If data is provided, add it to the request body
    if (data) {
      options.body = JSON.stringify(data);
    }

    const response = await fetch(`${BASE_URL}${endpoint}`, options);
    if (!response.ok) {
      await handleHttpError(response, endpoint, "DELETE");
    }

    // For DELETE requests, check if response has content before trying to parse JSON
    if (
      response.status === 204 ||
      response.headers.get("content-length") === "0"
    ) {
      return { success: true }; // Return a simple success object for empty responses
    }

    try {
      return await response.json();
    } catch (error) {
      // If JSON parsing fails, return a success indicator
      return { success: true };
    }
  },
  /**
   * GET request that returns the raw Response object (not parsed as JSON).
   * Used for binary responses like audio streams from TTS.
   */
  getRaw: async (endpoint) => {
    console.log(`[APIService] GET (raw) request to: ${BASE_URL}${endpoint}`);

    const { controller, timeoutId } = createTimeoutController(60000); // 60s for audio

    try {
      const headers = buildHeaders({}, "GET");
      const response = await fetch(`${BASE_URL}${endpoint}`, {
        headers,
        signal: controller.signal,
        credentials: "include",
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        console.error(
          `[APIService] GET (raw) request failed: ${response.status}`,
        );
        await handleHttpError(response, endpoint, "GET");
      }

      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === "AbortError") {
        const timeoutError = new Error("Request timed out");
        timeoutError.code = "TIMEOUT";
        throw timeoutError;
      }
      throw error;
    }
  },
  patch: async (endpoint, data) => {
    console.log(`${BASE_URL}${endpoint}`); // Log the endpoint being accessed
    console.log("APIService patch data:", data);
    console.log("APIService patch data (JSON):", JSON.stringify(data));

    const headers = buildHeaders(
      {
        "Content-Type": "application/json",
      },
      "PATCH",
    );

    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(data),
      credentials: "include", // Include cookies in request and response
    });
    if (!response.ok) {
      await handleHttpError(response, endpoint, "PATCH");
    }
    return response.json();
  },
};

export default APIService;
